import React, { createContext, useContext, useState, useCallback, ReactNode } from 'react';
import { projectService } from '../services/api';
import type { Project } from '../types';

interface DataCacheContextType {
    projects: Project[];
    articlesByProject: { [key: string]: any[] };
    articlesLoading: { [key: string]: boolean };
    lastUpdated: number;
    isLoading: boolean;
    error: string | null;
    refreshProjects: () => Promise<void>;
    refreshArticles: (projectId: string) => Promise<void>;
    loadAllData: () => Promise<void>;
    loadProjectArticles: (projectId: string) => Promise<any[]>;
    clearCache: () => void;
}

const DataCacheContext = createContext<DataCacheContextType | undefined>(undefined);

export const DataCacheProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
    const [projects, setProjects] = useState<Project[]>([]);
    const [articlesByProject, setArticlesByProject] = useState<{ [key: string]: any[] }>({});
    const [articlesLoading, setArticlesLoading] = useState<{ [key: string]: boolean }>({});
    const [lastUpdated, setLastUpdated] = useState<number>(0);
    const [isLoading, setIsLoading] = useState<boolean>(false);
    const [error, setError] = useState<string | null>(null);

    const CACHE_DURATION = 5 * 60 * 1000; // 5 minutes

    // OPTIMIZED: Only load projects on initial load, NOT all articles
    const loadAllData = useCallback(async (force = false) => {
        const now = Date.now();
        if (!force && lastUpdated > 0 && (now - lastUpdated < CACHE_DURATION) && projects.length > 0) {
            return;
        }

        setIsLoading(true);
        setError(null);
        try {
            const projectsData = await projectService.getProjects();
            setProjects(projectsData);
            setLastUpdated(now);
        } catch (err: any) {
            setError(err.message || 'Error al cargar datos');
        } finally {
            setIsLoading(false);
        }
    }, [lastUpdated, projects.length]);

    // LAZY LOAD: Load articles only when needed for a specific project
    const loadProjectArticles = useCallback(async (projectId: string): Promise<any[]> => {
        // Return cached if available
        if (articlesByProject[projectId] && articlesByProject[projectId].length > 0) {
            return articlesByProject[projectId];
        }

        // Don't load if already loading
        if (articlesLoading[projectId]) {
            return [];
        }

        setArticlesLoading(prev => ({ ...prev, [projectId]: true }));

        try {
            const articles = await projectService.getProjectArticles(projectId);
            setArticlesByProject(prev => ({
                ...prev,
                [projectId]: articles
            }));
            return articles;
        } catch (e) {
            return [];
        } finally {
            setArticlesLoading(prev => ({ ...prev, [projectId]: false }));
        }
    }, [articlesByProject, articlesLoading]);

    const refreshProjects = useCallback(async () => {
        await loadAllData(true);
    }, [loadAllData]);

    const refreshArticles = useCallback(async (projectId: string) => {
        // Force refresh by clearing cache first
        setArticlesByProject(prev => {
            const updated = { ...prev };
            delete updated[projectId];
            return updated;
        });
        await loadProjectArticles(projectId);
    }, [loadProjectArticles]);

    const clearCache = useCallback(() => {
        setProjects([]);
        setArticlesByProject({});
        setArticlesLoading({});
        setLastUpdated(0);
    }, []);

    return (
        <DataCacheContext.Provider value={{
            projects,
            articlesByProject,
            articlesLoading,
            lastUpdated,
            isLoading,
            error,
            refreshProjects,
            refreshArticles,
            loadAllData,
            loadProjectArticles,
            clearCache
        }}>
            {children}
        </DataCacheContext.Provider>
    );
};

export const useDataCache = () => {
    const context = useContext(DataCacheContext);
    if (context === undefined) {
        throw new Error('useDataCache must be used within a DataCacheProvider');
    }
    return context;
};
